import { reviewPathAfterEdit } from 'library/helpers/importEditFlow';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import { addBookingSettings, updateOnboardingStage } from 'features/placeSlice';
import BookingService from 'service/BookingService';
import PropertyService from 'service/PropertyService';
import { useOnboardingBackConfirm } from 'library/hooks/useOnboardingBackConfirm';
import { transformBookingSettingsFromApi } from 'library/helpers/bookingSettingsFromApi';
import { isAdminUser } from 'library/utils/isAdmin';
import {
  getApiResponseMessage,
  getUserFriendlyError,
} from 'library/helpers/errorMessage';
import {
  OnboardingShell,
  Field,
  Group,
  MultiChips,
  TextArea,
  TextInput,
} from 'components/Onboarding';

export const HOUSE_RULE_OPTIONS = [
  'No smoking',
  'No alcohol',
  'No events or parties',
  'No loud music after quiet hours',
  'Pets allowed',
  'Unmarried couples allowed',
  'Visitors allowed',
  'ID proof mandatory at check-in',
  'No commercial photography',
  'Quiet hours',
];

export const CUSTOM_RULES_LIMIT = 400;
export const QUIET_PREFIX = 'Quiet hours';
export const CUSTOM_PREFIX = 'Other rules: ';

const sameRule = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * House rules live in the booking settings' `restrictions` list (saved as `propertyPolicy`):
 * the chosen chips, "Quiet hours: <time>" and one "Other rules: <text>" entry for the free text.
 */
export function parseRestrictions(restrictions: string[] = []): {
  selected: string[];
  quietHours: string;
  customRules: string;
} {
  const selected: string[] = [];
  let quietHours = '';
  let customRules = '';
  restrictions.forEach((entry) => {
    if (typeof entry !== 'string') return;
    if (entry.startsWith(CUSTOM_PREFIX)) {
      customRules = entry.slice(CUSTOM_PREFIX.length);
      return;
    }
    if (entry.toLowerCase().startsWith(QUIET_PREFIX.toLowerCase())) {
      const rest = entry.slice(QUIET_PREFIX.length).replace(/^[:\s-]+/, '');
      quietHours = rest;
      if (!selected.includes(QUIET_PREFIX)) selected.push(QUIET_PREFIX);
      return;
    }
    const known = HOUSE_RULE_OPTIONS.find((option) => sameRule(option, entry));
    selected.push(known ?? entry);
  });
  return { selected, quietHours, customRules };
}

const HouseRules: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(useLocation().search);
  const hostPropertyId = searchParams.get('id');
  // Set by the Booking page when an existing listing is being edited (no hosting-plan step then).
  const isEdit = searchParams.get('edit') === '1';

  const { bookingSettings, propertyId, onboardingStage } = useSelector((state: any) => ({
    bookingSettings: state.bookingSettings,
    propertyId: state.propertyId,
    onboardingStage: state.onboardingStage,
  }));

  const [selected, setSelected] = useState<string[]>([]);
  const [quietHours, setQuietHours] = useState('');
  const [customRules, setCustomRules] = useState('');
  const [saving, setSaving] = useState(false);
  const [customTouched, setCustomTouched] = useState(false);

  const resolvePropertyId = (): string | number | null =>
    hostPropertyId ||
    propertyId ||
    localStorage.getItem('id') ||
    localStorage.getItem('newPropertId');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Make sure the saved booking settings are loaded (they hold the rules and everything else we re-send).
  useEffect(() => {
    const id = resolvePropertyId();
    if (!id || bookingSettings?.checkInTime) return;
    let cancelled = false;
    PropertyService.getBookingSettingsByPropertyId(id)
      .then((responseData: any) => {
        if (cancelled) return;
        const { responseInformation, result } = responseData;
        if ((responseInformation as any)?.responseCode === 0 && result) {
          const dataSet = transformBookingSettingsFromApi(result);
          if (dataSet) dispatch(addBookingSettings(dataSet as any));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPropertyId, propertyId, onboardingStage]);

  // Show what is already saved.
  useEffect(() => {
    const parsed = parseRestrictions(bookingSettings?.restrictions || []);
    setSelected(parsed.selected);
    setQuietHours(parsed.quietHours);
    setCustomRules(parsed.customRules);
  }, [bookingSettings]);

  const toggle = (rule: string): void =>
    setSelected((current) =>
      current.includes(rule) ? current.filter((item) => item !== rule) : [...current, rule],
    );

  const options = [
    ...HOUSE_RULE_OPTIONS,
    ...selected.filter((rule) => !HOUSE_RULE_OPTIONS.includes(rule)),
  ];

  const doBack = (): void => {
    const id = resolvePropertyId();
    navigate(
      isEdit && id ? `/host/booking-settings?id=${id}` : '/host/booking-settings',
    );
  };
  const { backHandler, BackConfirmModal } = useOnboardingBackConfirm(doBack);

  const save = async (): Promise<void> => {
    const id = resolvePropertyId();
    if (!id) {
      notification.error({
        message: 'Missing property id',
        description: 'Could not determine property id to save house rules. Please reload and try again.',
      });
      return;
    }
    if (!bookingSettings?.checkInTime || !bookingSettings?.checkOutTime) {
      notification.error({
        message: 'Booking settings missing',
        description: 'Please go back and set your booking details first.',
      });
      return;
    }

    const restrictions: string[] = selected.map((rule) =>
      rule === QUIET_PREFIX && quietHours.trim()
        ? `${QUIET_PREFIX}: ${quietHours.trim()}`
        : rule,
    );
    if (customRules.trim()) restrictions.push(`${CUSTOM_PREFIX}${customRules.trim()}`);

    setSaving(true);
    try {
      const res = await BookingService.savePropertyBookingSettings({
        propertyId: id,
        checkInTime: bookingSettings.checkInTime,
        checkOutTime: bookingSettings.checkOutTime,
        cancellationPolicy: JSON.stringify(bookingSettings.cancellationPolicy),
        propertyPolicy: JSON.stringify(restrictions),
        partialPaymentAllowed: bookingSettings.partialPaymentAllowed,
        partialPaymentPercentage: bookingSettings.partialPaymentPercentage,
        onboardingStage: 4,
        approvalRequiredForBooking: false,
      });
      if (res.responseInformation && res.responseInformation?.responseCode === 0) {
        dispatch(addBookingSettings({ ...bookingSettings, restrictions }));
        dispatch(updateOnboardingStage(4));
        navigate(
          isEdit
            ? reviewPathAfterEdit(id)
            : isAdminUser()
              ? `/host/acknowledgement?id=${id}` // admins skip the plan screen (plan-free listings)
              : `/host/hosting-plan?id=${id}`,
        );
      } else {
        notification.error({
          message: 'Unable to save house rules',
          description: getApiResponseMessage(res, 'Could not save house rules. Please try again.'),
        });
      }
    } catch (error: any) {
      notification.error({
        message: 'Unable to save house rules',
        description: getUserFriendlyError(error, 'Could not save house rules. Please try again.'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell
      section={7}
      title="Set your house rules"
      intro="Let guests know how your home works before they book."
      onBack={backHandler}
      onContinue={save}
      continueLabel={isEdit ? 'Review listing' : 'Continue'}
      continueLoading={saving}
      modal={BackConfirmModal}
    >
      <Group title="Rules and regulations">
        <MultiChips options={options} values={selected} onToggle={toggle} />
        {selected.includes(QUIET_PREFIX) ? (
          <Field label="Quiet hours">
            <TextInput
              value={quietHours}
              onChange={(event) => setQuietHours(event.target.value)}
              placeholder="22:00 – 08:00"
            />
          </Field>
        ) : null}
      </Group>

      <Group
        title="Anything else?"
        note="Mention all rules and regulations that the guests must know before check in."
      >
        <Field
          label="Your rules"
          hint={customTouched ? `${customRules.length}/${CUSTOM_RULES_LIMIT} characters` : undefined}
        >
          <TextArea
            value={customRules}
            maxLength={CUSTOM_RULES_LIMIT}
            style={{ minHeight: 112 }}
            onChange={(event) => {
              setCustomTouched(true);
              setCustomRules(event.target.value);
            }}
            placeholder="For example: please remove shoes indoors."
          />
        </Field>
      </Group>
    </OnboardingShell>
  );
};

export default HouseRules;
